/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Form,
  Input,
  InputGroup,
  Label,
  Title,
  LabelRequired,
  Line,
  Select,
} from "styles/common/FormStyle";
import CheckBox from "components/signup/CheckBox";
import { ButtonOutline } from "styles/common/ButtonStyle";
import { Background, CommonWrapper } from "styles/common/Layout";
import { signup, verify } from "api/user";
import useAuthStore from "store/auth";
import { API_URL } from "utils/constants";
import { Row, Col } from "react-bootstrap";
import useModalStore from "store/modal";
import DaumPostcode from "react-daum-postcode";

function SignUp() {
  const navigate = useNavigate();
  const authStore = useAuthStore();
  const modalStore = useModalStore();

  const [form, setForm] = useState({
    userId: "",
    name: "",
    password: "",
    confirmPassword: "",
    phoneNumber: "",
    postNo: "",
    address: "",
    addressDetail: "",
    aptName: "",
    gender: "",
    birthday: undefined,
    locationAgree: false,
    marketingAgree: false,
  });

  const optional = {
    addressDetail: true,
    aptName: true,
    gender: true,
    birthday: true,
    locationAgree: true,
    marketingAgree: true,
  };

  const [requiredAgreements, setRequiredAgreements] = useState({
    termsAgree: false,
    privacyAgree: false,
    thirdPartyAgree: false,
    commissionAgree: false,
  });

  const [page, setPage] = useState("terms");
  // 상태 선언
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [day, setDay] = useState("");
  const [daysInMonth, setDaysInMonth] = useState([]);

  // 연도 목록 생성 (1920년부터 현재까지)
  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 1920; i <= currentYear; i++) {
      years.push(i);
    }
    return years;
  };

  // 월별 일수를 계산하는 함수
  const getDaysInMonth = (year, month) => {
    if (!year || !month) return [];
    const days = new Date(year, month, 0).getDate(); // 해당 월의 마지막 날짜
    const dayOptions = [];
    for (let i = 1; i <= days; i++) {
      dayOptions.push(i);
    }
    return dayOptions;
  };

  // 월이나 연도가 변경되면 일수를 업데이트
  useEffect(() => {
    const newDaysInMonth = getDaysInMonth(year, month);
    setDaysInMonth(newDaysInMonth);

    if (day && !newDaysInMonth.includes(Number(day))) {
      setDay(""); // day 값을 리셋
    }

    // 생년월일이 완성되면 Date 객체로 설정
    if (year && month && day && newDaysInMonth.includes(Number(day))) {
      console.log(new Date(year, month - 1, day));
      setForm({ ...form, birthday: new Date(year, month - 1, day) });
    }
  }, [year, month, day]);

  const isCompleted = () => {
    return (
      Object.keys(form).every((key) => form[key] || key in optional) &&
      form.password === form.confirmPassword
    );
  };

  const isRequiredAgreed = Object.keys(requiredAgreements).every(
    (key) => requiredAgreements[key]
  );

  const isAllAgreed =
    isRequiredAgreed && form.locationAgree && form.marketingAgree;

  const handleChange = (event) => {
    if (requiredAgreements[event.target.name] !== undefined) {
      setRequiredAgreements({
        ...requiredAgreements,
        [event.target.name]: event.target.checked,
      });
      return;
    }
    if (event.target.type === "checkbox") {
      setForm({ ...form, [event.target.name]: event.target.checked });
      return;
    }
    setForm({ ...form, [event.target.name]: event.target.value });
  };

  const checkUserId = async (userId) => {
    try {
      const response = await verify(userId);

      if (response?.result === true) {
        alert("사용 가능한 아이디입니다.");
      } else {
        alert("이미 사용중인 아이디입니다.");
      }
    } catch (error) {
      alert(error);
    }
  };

  const searchAddress = () => {
    modalStore.openModal({
      title: "주소 검색",
      content: <DaumPostcode onComplete={handleComplete} />,
    });
  };

  const handleComplete = (data) => {
    setForm({
      ...form,
      postNo: data?.zonecode,
      address: data?.roadAddress ?? data?.autoRoadAddress ?? data?.address,
      aptName: data?.apartment ? data?.buildingName : "",
    });
    modalStore.closeModal();
  };

  const agreeAll = (event) => {
    const { checked } = event.target;
    setRequiredAgreements({
      termsAgree: checked,
      privacyAgree: checked,
      thirdPartyAgree: checked,
      commissionAgree: checked,
    });

    setForm({
      ...form,
      locationAgree: checked,
      marketingAgree: checked,
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const {
      userId,
      name,
      password,
      confirmPassword,
      phoneNumber,
      postNo,
      address,
      addressDetail,
      aptName,
      birthday,
      gender,
      locationAgree,
      marketingAgree,
    } = form;

    if (password !== confirmPassword) {
      alert("비밀번호가 일치하지 않습니다.");
    }

    if (!isCompleted()) {
      alert("필수 항목을 모두 입력해주세요.");
      return;
    }

    try {
      const response = await signup({
        userId,
        name,
        password,
        confirmPassword,
        phoneNumber,
        postNo,
        address,
        addressDetail,
        aptName,
        birthday,
        gender,
        locationAgree,
        marketingAgree,
      });

      if (response?.token) {
        const token = response?.token;
        const user = response?.user;
        const refreshToken = response?.refreshToken;

        authStore?.login(user, token, refreshToken);
        alert("회원가입이 완료되었습니다.");
        navigate("/");
      }
    } catch (error) {
      alert(error);
    }
  };

  return (
    <Background>
      <CommonWrapper>
        {page === "terms" ? (
          <Form>
            <Title>이용약관</Title>
            <CheckBox
              checked={isAllAgreed}
              onChange={agreeAll}
              label="모든 약관에 동의합니다."
              marginBottom="0"
            />
            <Line />
            <CheckBox
              id="termsAgree"
              name="termsAgree"
              checked={requiredAgreements.termsAgree}
              onChange={handleChange}
              label="서비스 이용약관 동의 (필수)"
              href={API_URL.TERMS_URL}
            />
            <CheckBox
              id="privacyAgree"
              name="privacyAgree"
              checked={requiredAgreements.privacyAgree}
              onChange={handleChange}
              label="개인정보 수집 및 이용동의 (필수)"
              href={API_URL.PRIVACY_URL}
            />
            {/* TODO: 링크 추가 협의 */}
            <CheckBox
              id="commissionAgree"
              name="commissionAgree"
              checked={requiredAgreements.commissionAgree}
              onChange={handleChange}
              label="개인정보 취급 위탁 동의 (필수)"
            />
            <CheckBox
              id="thirdPartyAgree"
              name="thirdPartyAgree"
              checked={requiredAgreements.thirdPartyAgree}
              onChange={handleChange}
              label="개인정보 제3자 제공 동의 (필수)"
            />
            <CheckBox
              id="locationAgree"
              name="locationAgree"
              checked={form.locationAgree}
              onChange={handleChange}
              label="위치정보 수집 및 이용동의 (선택)"
            />
            <CheckBox
              id="marketingAgree"
              name="marketingAgree"
              checked={form.marketingAgree}
              onChange={handleChange}
              label="마케팅 정보 수신 동의 (선택)"
            />
            <ButtonOutline
              type="button"
              margin="10px 0 40px 0"
              disabled={!isRequiredAgreed}
              onClick={() => setPage("signup")}
            >
              확인
            </ButtonOutline>
          </Form>
        ) : (
          <Form onSubmit={handleSubmit} id="contents" style={{ paddingBottom: "3rem" }}>
            <Title>회원가입</Title>
            <LabelRequired htmlFor="userId">아이디</LabelRequired>
            <InputGroup>
              <Input
                type="userId"
                id="userId"
                name="userId"
                value={form.userId}
                onChange={handleChange}
                placeholder="아이디를 입력해주세요."
              />
              <ButtonOutline
                type="button"
                onClick={() => checkUserId(form.userId)}
                width="130px"
                height="40px"
                margin="0 0 0 10px"
              >
                중복확인
              </ButtonOutline>
            </InputGroup>
            <LabelRequired htmlFor="password">비밀번호</LabelRequired>
            <Input
              type="password"
              id="password"
              name="password"
              value={form.password}
              onChange={handleChange}
              placeholder="비밀번호를 입력해주세요."
            />
            <LabelRequired htmlFor="confirmPassword">
              비밀번호 확인
            </LabelRequired>
            <Input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={form.confirmPassword}
              onChange={handleChange}
              placeholder="비밀번호를 확인해주세요."
            />
            <LabelRequired htmlFor="phoneNumber">전화번호</LabelRequired>
            <InputGroup>
              <Input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={form.phoneNumber}
                onChange={handleChange}
                placeholder="전화번호를 입력해주세요."
              />
            </InputGroup>
            <LabelRequired htmlFor="name">이름</LabelRequired>
            <Input
              type="name"
              id="name"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="이름을 입력해주세요."
            />
            <LabelRequired htmlFor="confirmPassword">주소</LabelRequired>
            <InputGroup margin="0 0 5px 0">
              <Input
                type="text"
                id="postNo"
                name="postNo"
                value={form.postNo}
                placeholder="우편번호"
                disabled
              />
              <ButtonOutline
                type="button"
                width="130px"
                height="40px"
                margin="0 0 0 10px"
                onClick={searchAddress}
              >
                주소 검색
              </ButtonOutline>
            </InputGroup>
            <Input
              type="text"
              id="address"
              name="address"
              value={form.address}
              onChange={handleChange}
              placeholder="주소"
              marginBottom="5px"
              disabled
            />
            <Input
              type="text"
              id="addressDetail"
              name="addressDetail"
              value={form.addressDetail}
              onChange={handleChange}
              placeholder="상세주소"
            />
            <Row>
              <Col xs="100" md="4">
                <Label htmlFor="name">성별</Label>
                <Select
                  height="40px"
                  id="gender"
                  name="gender"
                  onChange={handleChange}
                  className={form.gender === "" ? "default" : ""}
                  defaultValue=""
                >
                  <option value="" disabled>
                    성별
                  </option>
                  <option value="m">남</option>
                  <option value="f">여</option>
                </Select>
              </Col>
              <Col md="8">
                <Label htmlFor="birthday">생년월일</Label>
                <InputGroup margin="0 0 5px 0" gap="5px">
                  <Select
                    id="year"
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                  >
                    <option value="">연도 선택</option>
                    {generateYears().map((yr) => (
                      <option key={yr} value={yr}>
                        {yr}
                      </option>
                    ))}
                  </Select>
                  <Select
                    id="month"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                  >
                    <option value="">월 선택</option>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </Select>
                  <Select
                    id="day"
                    value={day}
                    onChange={(e) => setDay(e.target.value)}
                  >
                    <option value="">일 선택</option>
                    {daysInMonth.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </Select>
                </InputGroup>
              </Col>
            </Row>
            <ButtonOutline
              type="submit"
              margin="0 0 40px 0"
              disabled={!isCompleted()}
            >
              회원가입
            </ButtonOutline>
          </Form>
        )}
      </CommonWrapper>
    </Background>
  );
}

export default SignUp;
